import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, ConfirmDialog, useToast } from '../../../components/ui';
import { customerFilter, dateFilter } from '../../../components/common/ListPage/listFilters';
import { sendOnWhatsApp } from '../../../lib/share';
import { Cell, ListPage } from '../../../components/common/ListPage/ListPage';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { QUOTATION_STATUS } from '../../../constants/status';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { quotationService } from '../services/quotationService';
import type { ReactNode } from 'react';
import type { Quotation } from '../types/quotationTypes';

const TABS = [{ value: '', label: 'All' }, ...Object.entries(QUOTATION_STATUS).map(([value, [label]]) => ({ value, label }))];
const DAY = 86400000;
const daysLeft = (d: string | Date) => Math.ceil((new Date(d).getTime() - Date.now()) / DAY);

// The natural next step for each status, shown in the row menu.
const NEXT = {
  draft: ['sent', 'Mark as sent', 'send'],
  sent: ['approved', 'Mark accepted', 'checkCircle'],
  expired: ['sent', 'Send again', 'send'],
  rejected: ['draft', 'Back to draft', 'refresh'],
};

function Stat({ label, value, note, tone }: { label: string; value: ReactNode; note?: ReactNode; tone?: string }) {
  return (
    <Card className="p-5">
      <p className="text-sm text-ink-500">{label}</p>
      <p className={cn('tabular mt-1.5 text-2xl font-semibold tracking-tight text-navy-900', tone)}>{value}</p>
      <p className="mt-1 truncate text-[13px] text-ink-500">{note}</p>
    </Card>
  );
}

/** KPI cards from the latest 200 quotations. */
function QuoteStats() {
  const { data } = useFetch(() => quotationService.list({ limit: 200 }).then((r) => r.data), []);
  const list = data ?? [];
  const sent = list.filter((q) => q.status === 'sent');
  const expiring = list.filter((q) => ['draft', 'sent'].includes(q.status) && q.validTill && daysLeft(q.validTill) >= 0 && daysLeft(q.validTill) <= 7);
  const won = list.filter((q) => ['approved', 'converted'].includes(q.status));
  const decided = won.length + list.filter((q) => q.status === 'rejected').length;
  const sum = (arr) => arr.reduce((a, q) => a + (q.grandTotal ?? 0), 0);
  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
      <Stat label="Awaiting reply" value={data ? money(sum(sent)) : '…'} note={`${sent.length} sent`} />
      <Stat label="Expiring in 7 days" value={data ? expiring.length : '…'} tone={expiring.length ? 'text-sun-600' : undefined} note={expiring.map((q) => q.customer?.name).slice(0, 3).join(', ') || 'None'} />
      <Stat label="Won" value={data ? money(sum(won)) : '…'} tone="text-emerald-700" note={`${won.length} accepted or job created`} />
      <Stat label="Win rate" value={decided ? `${Math.round((won.length / decided) * 100)}%` : '-'} note={`of ${decided} decided quotes`} />
    </div>
  );
}

export default function QuotationList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [deleting, setDeleting] = useState<{ q: Quotation; reload: () => void } | null>(null);
  const add = <Button leftIcon="plus" onClick={() => navigate('/quotations/new')}>New quotation</Button>;

  const rowActions = (q, { reload }) => {
    const next = NEXT[q.status];
    return [
      { label: 'View', icon: 'eye', onClick: () => navigate(`/quotations/${q._id}`) },
      ['draft', 'sent'].includes(q.status) && { label: 'Edit', icon: 'edit', onClick: () => navigate(`/quotations/${q._id}/edit`) },
      {
        label: 'Duplicate',
        icon: 'copy',
        onClick: async () => {
          const copy = await run('dup', () => quotationService.duplicate(q._id), 'Copy made as a new draft');
          if (copy && copy !== true) navigate(`/quotations/${copy._id}`);
        },
      },
      { label: 'Download PDF', icon: 'download', onClick: () => run('pdf', () => quotationService.openPdf(q._id)) },
      {
        label: 'Send on WhatsApp',
        icon: 'send',
        onClick: () => run('wa', () => sendOnWhatsApp('quotations', q._id, q.customer?.phone, `Hello ${q.customer?.name}, please find our quotation ${q.quotationNo}: ${money(q.grandTotal)}.`)),
      },
      next && {
        label: next[1],
        icon: next[2],
        onClick: async () => (await run('status', () => quotationService.setStatus(q._id, next[0]), `${q.quotationNo}: ${labelOf(QUOTATION_STATUS, next[0])}`)) && reload(),
      },
      q.status === 'approved' && { label: 'Create job', icon: 'wrench', onClick: () => navigate(`/quotations/${q._id}/convert`) },
      q.status === 'converted' && q.serviceId && { label: 'Open job', icon: 'wrench', onClick: () => navigate(`/jobs/${q.serviceId}`) },
      q.status === 'draft' && { divider: true },
      q.status === 'draft' && { label: 'Delete', icon: 'trash', danger: true, onClick: () => setDeleting({ q, reload }) },
    ].filter(Boolean);
  };

  const remove = async () => {
    if (!deleting) return;
    if (await run('delete', () => quotationService.remove(deleting.q._id), `${deleting.q.quotationNo} deleted`)) deleting.reload();
    setDeleting(null);
  };

  return (
    <>
      <ListPage
        title="Quotations"
        path="/quotations"
        tabs={TABS}
        filters={[dateFilter(), customerFilter]}
        stats={<QuoteStats />}
        searchPlaceholder="Quote no, customer, job"
        actions={add}
        rowTo={(q) => `/quotations/${q._id}`}
        rowActions={rowActions}
        columns={[
          { header: 'Quote no.', sort: 'quotationNo', sortDesc: true, render: (q) => q.quotationNo, csv: (q) => q.quotationNo },
          { header: 'Customer / job', sort: 'customer.name', render: (q) => <Cell title={q.customer?.name} sub={q.title} />, csv: (q) => q.customer?.name },
          { header: 'Job', sort: 'title', render: (q) => q.title || '-', csv: (q) => q.title, defaultHidden: true },
          { header: 'Status', sort: 'status', render: (q) => <StatusBadge map={QUOTATION_STATUS} value={q.status} />, csv: (q) => labelOf(QUOTATION_STATUS, q.status) },
          { header: 'Date', sort: 'date', sortDesc: true, render: (q) => formatDate(q.date), csv: (q) => formatDate(q.date) },
          {
            header: 'Valid until', sort: 'validTill', sortDesc: true,
            render: (q) => {
              const left = q.validTill ? daysLeft(q.validTill) : null;
              const soon = ['draft', 'sent'].includes(q.status) && left != null && left >= 0 && left <= 7;
              return (
                <>
                  {formatDate(q.validTill)}
                  {soon && <span className="block text-xs text-sun-600">{left === 0 ? 'Today' : `In ${left} day${left > 1 ? 's' : ''}`}</span>}
                </>
              );
            },
            csv: (q) => formatDate(q.validTill),
          },
          { header: 'Amount', sort: 'grandTotal', sortDesc: true, align: 'right', render: (q) => <span className="font-semibold text-navy-900">{money(q.grandTotal)}</span>, csv: (q) => (q.grandTotal ?? 0) / 100 },
        ]}
        mobile={(q) => ({
          title: q.customer?.name,
          subtitle: [q.quotationNo, q.title, formatDate(q.date)].filter(Boolean).join(' · '),
          right: money(q.grandTotal),
          badge: <StatusBadge map={QUOTATION_STATUS} value={q.status} />,
        })}
        empty={{ icon: 'quote', title: 'No quotations yet', message: 'Make your first quotation with labour and a material list.', action: add }}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title={`Delete ${deleting?.q.quotationNo}?`}
        message="The draft will be removed for good."
        confirmLabel="Delete quotation"
      />
    </>
  );
}
